import { useCallback, useState } from "react";

import { useUpgradePrompt } from "@skilitics/designer-access-client";
import {
  LoadingContainer,
  LoadingIndicator,
  type ModalDialogProps,
  useModalDialog,
} from "@skilitics/designer-ui-components";

import { useDesignerPermission } from "./useDesignerPermission.ts";

type PermissionName = "saveTemplates";
const notPending = Symbol("not pending");
export const useShowDialogWithPermissionCheck = <ShowOptions = void,>(
  permission: PermissionName,
  getProps: (options: ShowOptions, closeModalDialog: () => void) => ModalDialogProps,
  dialogId = permission,
): ((options: ShowOptions) => void) => {
  const hasPermission = useDesignerPermission("saveTemplates");
  const showUpgradePrompt = useUpgradePrompt("saveTemplates");
  const { openModalDialog, closeModalDialog } = useModalDialog();
  // need to *not* pass the event to closeModalDialog(), or it will try to close the
  // dialog with the id == String(event).
  const boundCloseModalDialog = useCallback(() => closeModalDialog(dialogId), [closeModalDialog, dialogId]);
  const [pendingOptions, setPendingOptions] = useState<ShowOptions | typeof notPending>(notPending);

  // Apparently this is better than using useEffect() according to the lints:
  // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  if (pendingOptions !== notPending && hasPermission !== undefined) {
    setPendingOptions(notPending);
    if (hasPermission) {
      openModalDialog(getProps(pendingOptions, boundCloseModalDialog), dialogId);
    } else {
      showUpgradePrompt(dialogId);
    }
  }

  return useCallback(
    (options: ShowOptions) => {
      if (hasPermission === null) {
        setPendingOptions(options);
        openModalDialog(
          <LoadingContainer>
            <LoadingIndicator />
          </LoadingContainer>,
          dialogId,
        );
      } else if (hasPermission) {
        openModalDialog(getProps(options, boundCloseModalDialog), dialogId);
      } else {
        showUpgradePrompt(dialogId);
      }
    },
    [hasPermission, showUpgradePrompt, getProps, openModalDialog, boundCloseModalDialog, dialogId],
  );
};
